import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Chip } from '@mui/material';
import { Close, ArrowForward, ArrowBack } from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

interface SpotlightStep {
  target: string;
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export const PAGE_GUIDES: Record<string, { title: string; color: string; steps: SpotlightStep[] }> = {
  '/': {
    title: 'Dashboard',
    color: '#3B82F6',
    steps: [
      {
        target: '[data-tutorial="dashboard-metrics"]',
        title: 'Usage Metrics',
        description: 'Real-time platform stats — total apps registered, users, and admin role breakdown at a glance.',
        position: 'bottom',
      },
      {
        target: '[data-tutorial="dashboard-apps"]',
        title: 'Recent Applications',
        description: 'Your registered apps and their token usage today. Click any app to drill into its details.',
        position: 'right',
      },
      {
        target: '[data-tutorial="dashboard-usage"]',
        title: 'Token Consumption',
        description: 'Per-app token breakdown — cyan bars are input tokens, purple are output tokens.',
        position: 'left',
      },
    ],
  },
  '/apps': {
    title: 'Applications',
    color: '#8B5CF6',
    steps: [
      {
        target: '[data-tutorial="apps-register-btn"]',
        title: 'Register a New App',
        description: 'Opens the registration form — set the app name, department, token limits, allowed models, and endpoint permissions.',
        position: 'bottom',
      },
      {
        target: '[data-tutorial="apps-table"]',
        title: 'Applications Table',
        description: 'Every registered app is listed here. Use the eye icon to view full details, edit settings, or reveal the auth key (super admins only).',
        position: 'top',
      },
    ],
  },
  '/users': {
    title: 'User Management',
    color: '#EC4899',
    steps: [
      {
        target: '[data-tutorial="users-invite"]',
        title: 'Invite a User',
        description: 'Search your directory and invite a new admin, viewer, or app viewer. Set their role and individual permissions here.',
        position: 'bottom',
      },
      {
        target: '[data-tutorial="users-table"]',
        title: 'Users Table',
        description: 'All platform users — role badge, active status, creation date, and last login. Use the action icons to edit or remove users.',
        position: 'top',
      },
    ],
  },
  '/logs': {
    title: 'Activity Logs',
    color: '#F97316',
    steps: [
      {
        target: '[data-tutorial="logs-refresh"]',
        title: 'Refresh Logs',
        description: 'Pull the latest activity entries. Logs update in real time but click refresh to sync manually.',
        position: 'bottom',
      },
      {
        target: '[data-tutorial="logs-table"]',
        title: 'Activity Log Table',
        description: 'Every admin action on the platform is recorded here — who did what, on which resource, and whether it succeeded.',
        position: 'top',
      },
    ],
  },
};

const CALLOUT_W = 300;
const CALLOUT_H = 210; // approx max height of the callout card
const PAD       = 12;
const GAP       = 16;
const HEADER_H  = 60;  // layout top bar + safe gap
const MARGIN    = 6;

function calloutStyle(rect: DOMRect, pos: SpotlightStep['position']): React.CSSProperties {
  const ww = window.innerWidth;
  const wh = window.innerHeight;

  // Use clamped ring bounds (same clamp logic as the ring itself)
  const rTop    = Math.max(MARGIN, rect.top  - PAD);
  const rBottom = Math.min(wh - MARGIN, rect.bottom + PAD);
  const midX    = rect.left + rect.width / 2;
  const midY    = (rTop + rBottom) / 2;

  const spaceBelow = wh - rBottom - GAP;
  const spaceAbove = rTop - GAP - HEADER_H;

  const clampX = (x: number) => Math.max(16, Math.min(x, ww - CALLOUT_W - 16));
  const clampY = (y: number) => Math.max(HEADER_H, Math.min(y, wh - CALLOUT_H - 10));

  // If preferred position doesn't fit, fall back to the side with more room
  let effective = pos ?? 'bottom';
  if (effective === 'top'    && spaceAbove < CALLOUT_H) effective = 'bottom';
  if (effective === 'bottom' && spaceBelow < CALLOUT_H) effective = spaceAbove >= CALLOUT_H ? 'top' : 'bottom';

  switch (effective) {
    case 'top':
      return { top: clampY(rTop - CALLOUT_H - GAP), left: clampX(midX - CALLOUT_W / 2) };
    case 'right':
      return { top: clampY(midY - CALLOUT_H / 2), left: Math.min(rect.right + PAD + GAP, ww - CALLOUT_W - 16) };
    case 'left':
      return { top: clampY(midY - CALLOUT_H / 2), right: Math.min(ww - rect.left + PAD + GAP, ww - 16) };
    default: // bottom
      return { top: clampY(rBottom + GAP), left: clampX(midX - CALLOUT_W / 2) };
  }
}

interface Props {
  open: boolean;
  route: string;
  onClose: () => void;
}

const SpotlightGuide: React.FC<Props> = ({ open, route, onClose }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [step, setStep]       = useState(0);
  const [rect, setRect]       = useState<DOMRect | null>(null);
  const [animKey, setAnimKey] = useState(0);
  const [ready, setReady]     = useState(false);
  const lastEl = useRef<HTMLElement | null>(null);

  const guide   = PAGE_GUIDES[route];
  const current = guide?.steps[step];
  const isLast  = step === (guide?.steps.length ?? 1) - 1;

  const bg    = isDark ? '#1C1C1C' : '#FFFFFF';
  const textP = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS = isDark ? '#888' : '#666';
  const color = guide?.color ?? '#FF1A50';

  // Elevate element and measure
  const activate = useCallback((selector: string) => {
    setReady(false);
    if (lastEl.current) {
      lastEl.current.style.position = '';
      lastEl.current.style.zIndex   = '';
      lastEl.current = null;
    }
    setRect(null);
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) {
      // No element found — show fallback after the same delay so nothing flashes early
      setTimeout(() => { setAnimKey(k => k + 1); setReady(true); }, 320);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      el.style.position = 'relative';
      el.style.zIndex   = '1401';
      lastEl.current = el;
      setRect(el.getBoundingClientRect());
      setAnimKey(k => k + 1); // increment AFTER rect is ready so ring+callout appear together
      setReady(true);
    }, 320);
  }, []);

  // Activate on step change
  useEffect(() => {
    if (!open || !current) return;
    activate(current.target);
  }, [open, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up when closed
  useEffect(() => {
    if (open) { setStep(0); return; }
    if (lastEl.current) {
      lastEl.current.style.position = '';
      lastEl.current.style.zIndex   = '';
      lastEl.current = null;
    }
    setRect(null);
    setReady(false);
  }, [open]);

  // Re-measure on scroll / resize
  useEffect(() => {
    if (!open || !current) return;
    const update = () => {
      const el = lastEl.current;
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, current?.target]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); }
      else if (e.key === 'ArrowRight' && !isLast) { setAnimKey(k => k + 1); setStep(s => s + 1); }
      else if (e.key === 'ArrowLeft' && step > 0)  { setAnimKey(k => k + 1); setStep(s => s - 1); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, step, isLast, onClose]);

  if (!open || !guide) return null;

  const cStyle = rect && current ? calloutStyle(rect, current.position) : {};

  const goNext = () => { if (isLast) { onClose(); } else { setAnimKey(k => k + 1); setStep(s => s + 1); } };
  const goBack = () => { if (step > 0) { setAnimKey(k => k + 1); setStep(s => s - 1); } };

  return (
    <>
      {/* Dimmed backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed', inset: 0, zIndex: 1400,
          bgcolor: 'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(1.5px)',
          cursor: 'pointer',
          animation: 'fadeIn 0.2s ease',
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      />

      {/* Highlight ring — clamped to viewport so tall elements don't overflow */}
      {ready && rect && (() => {
        const MARGIN = 6;
        const rTop    = Math.max(MARGIN, rect.top - PAD);
        const rLeft   = Math.max(MARGIN, rect.left - PAD);
        const rBottom = Math.min(window.innerHeight - MARGIN, rect.bottom + PAD);
        const rRight  = Math.min(window.innerWidth  - MARGIN, rect.right  + PAD);
        return (
        <Box
          key={`ring-${animKey}`}
          sx={{
            position: 'fixed',
            top: rTop,
            left: rLeft,
            width: rRight - rLeft,
            height: rBottom - rTop,
            borderRadius: '10px',
            border: `2px solid ${color}`,
            boxShadow: `0 0 0 4px ${color}20, 0 0 28px ${color}50`,
            zIndex: 1402,
            pointerEvents: 'none',
            animation: 'ringPop 0.32s cubic-bezier(0.34,1.56,0.64,1)',
            '@keyframes ringPop': {
              from: { opacity: 0, transform: 'scale(0.9)' },
              to:   { opacity: 1, transform: 'scale(1)' },
            },
          }}
        />
        );
      })()}

      {/* Callout card */}
      {ready && rect && <Box
        key={`callout-${animKey}`}
        onClick={e => e.stopPropagation()}
        sx={{
          position: 'fixed',
          ...cStyle,
          width: CALLOUT_W,
          bgcolor: bg,
          border: `1px solid ${color}28`,
          borderLeft: `3px solid ${color}`,
          borderRadius: '12px',
          p: 2,
          zIndex: 1403,
          boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.65)' : '0 12px 40px rgba(0,0,0,0.14)',
          animation: 'calloutIn 0.22s ease',
          '@keyframes calloutIn': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1 }}>
          <Chip
            label={`${step + 1}/${guide.steps.length}`}
            size="small"
            sx={{ bgcolor: `${color}18`, color, fontWeight: 700, fontSize: '0.65rem', height: 18, border: 'none', flexShrink: 0, mt: '1px' }}
          />
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: textP, flex: 1, lineHeight: 1.25 }}>
            {current?.title}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25, color: textS, '&:hover': { color: '#EF4444' }, flexShrink: 0 }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Description */}
        <Typography sx={{ fontSize: '0.8rem', color: textS, lineHeight: 1.6, mb: 1.75 }}>
          {current?.description ?? 'Navigate to this page to see the element.'}
        </Typography>

        {/* Nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            disabled={step === 0}
            onClick={goBack}
            startIcon={<ArrowBack sx={{ fontSize: 12 }} />}
            sx={{ fontSize: '0.72rem', textTransform: 'none', color: textS, px: 0.75, minWidth: 0, '&.Mui-disabled': { opacity: 0.3 } }}
          >
            Back
          </Button>

          {/* Dots */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 0.4 }}>
            {guide.steps.map((_, i) => (
              <Box
                key={i}
                onClick={() => { setAnimKey(k => k + 1); setStep(i); }}
                sx={{
                  width: i === step ? 14 : 5, height: 5, borderRadius: 3, cursor: 'pointer',
                  bgcolor: i === step ? color : (isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)'),
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Box>

          <Button
            size="small"
            onClick={goNext}
            endIcon={!isLast ? <ArrowForward sx={{ fontSize: 12 }} /> : undefined}
            sx={{
              fontSize: '0.72rem', textTransform: 'none', px: 1.25,
              bgcolor: color, color: '#fff',
              '&:hover': { bgcolor: color, filter: 'brightness(1.1)' },
              boxShadow: `0 2px 8px ${color}44`,
            }}
          >
            {isLast ? 'Done ✓' : 'Next'}
          </Button>
        </Box>

        <Typography sx={{ fontSize: '0.6rem', color: isDark ? '#333' : '#DDD', textAlign: 'center', mt: 1 }}>
          ← → keys · Esc to close
        </Typography>
      </Box>}

      {/* Fallback: element not found */}
      {ready && !rect && current && (
        <Box
          onClick={e => e.stopPropagation()}
          sx={{
            position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
            width: 340, bgcolor: bg, borderLeft: `3px solid ${color}`,
            border: `1px solid ${color}28`, borderRadius: '12px', p: 2,
            zIndex: 1403, boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: textP, mb: 0.5 }}>
            {current.title}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: textS, mb: 1.5 }}>
            {current.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} sx={{ textTransform: 'none', color: textS, fontSize: '0.75rem' }}>Close</Button>
            {!isLast && (
              <Button size="small" onClick={goNext}
                sx={{ textTransform: 'none', bgcolor: color, color: '#fff', fontSize: '0.75rem', '&:hover': { bgcolor: color } }}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default SpotlightGuide;
